import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'wouter';

import useWindowSize from '../../hooks/useWindowSize';

import { useModal } from '../../contexts/ModalContext';
import backgroundh264portrait from '../../videos/FOND_KIOSQUE_9x16.mp4';
import backgroundh264wide from '../../videos/FOND_KIOSQUE_16x9.mp4';
import Button from '../buttons/Button';
import Cookie from '../icons/Cookie';
import Consent from '../modals/Consent';

import logoWhite from '../../img/la-poste-white.png';
import logo from '../../img/la-poste.png';
import styles from '../../styles/layouts/main.module.css';
import backgroundwebmportrait from '../../videos/FOND_KIOSQUE_9x16.webm';
import backgroundwebmwide from '../../videos/FOND_KIOSQUE_16x9.webm';

interface MainLayoutProps {
    fullScreen?: boolean;
    children?: React.ReactNode;
}

function MainLayout({ fullScreen = false, children = null }: MainLayoutProps) {
    const route = useUrlGenerator();
    const { modal = null, setModal = null } = useModal();
    const onClickCookie = useCallback(() => {
        if (setModal !== null) {
            setModal('consent');
        }
    }, [setModal]);

    const { width = 0, height = 0 } = useWindowSize();
    const widescreen = width / height >= 1;

    const large = useMemo(() => {
        return width >= 900;
    }, [width]);

    const video = useMemo(() => {
        return widescreen
            ? {
                  h264: backgroundh264wide,
                  webm: backgroundwebmwide,
              }
            : {
                  h264: backgroundh264portrait,
                  webm: backgroundwebmportrait,
              };
    }, [widescreen]);

    return (
        <div
            className={classNames([styles.container, { [styles.fullScreen]: fullScreen === true }])}
        >
            <div className={styles.header}>
                <a
                    className={styles.logoLink}
                    href="https://www.lapostegroupe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img className={styles.logo} src={logo} alt="La Poste" />
                </a>
                <Link className={styles.titleLink} href="/">
                    <h1 className={styles.title}>
                        Les postiers
                        {large ? <br /> : ' '}
                        de l'année 2025
                    </h1>
                </Link>
            </div>
            <div className={styles.inner}>
                <div className={styles.content}>{children}</div>
            </div>
            <div className={styles.footer}>
                <div className={styles.items}>
                    <Link className={styles.link} href={route('legal')} transition>
                        Mentions légales
                    </Link>{' '}
                    <Link className={styles.link} href={route('accessibility')} transition>
                        Accessibilité&nbsp;: non conforme
                    </Link>{' '}
                    <a
                        className={styles.link}
                        href="https://www.lapostegroupe.com/fr/politique-de-protection-des-donnees-personnelles"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Politique de données
                    </a>
                    <button className={styles.link} onClick={onClickCookie}>
                        Gérer les cookies
                    </button>
                </div>
                <a
                    className={styles.logoLink}
                    href="https://www.lapostegroupe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img className={styles.logo} src={logoWhite} alt="La Poste" />
                </a>
            </div>
            <video className={styles.video} autoPlay loop muted playsInline preload="auto">
                <source src={`${video.h264}#t=0.01`} type="video/mp4" />
                <source src={`${video.webm}#t=0.01`} type="video/webm" />
            </video>
            {modal === 'consent' ? (
                <Consent className={styles.consentModal} />
            ) : (
                <Button className={styles.cookies} onClick={onClickCookie}>
                    <Cookie className={styles.cookieIcon} />
                </Button>
            )}
        </div>
    );
}

export default MainLayout;
